import React, { useState } from 'react';
import { X, Lock, Loader2, Check, Eye, EyeOff } from 'lucide-react';
import { adminApi } from '../lib/api';

const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm(''); setError(''); setSuccess(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) return setError('Le nouveau mot de passe doit contenir au moins 8 caractères');
    if (next !== confirm) return setError('Les mots de passe ne correspondent pas');
    if (next === current) return setError('Le nouveau mot de passe doit être différent');
    setLoading(true);
    try {
      await adminApi.changePassword(current, next);
      setSuccess(true);
      setTimeout(() => { reset(); onClose(); }, 1600);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => { reset(); onClose(); }} />

      <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="bg-gradient-to-br from-[#F4A340] to-[#E89534] px-6 py-5 text-white flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <h2 className="text-xl font-bold">Changer le mot de passe</h2>
            </div>
            <p className="text-white/90 text-xs mt-1">Choisissez un mot de passe fort (min. 8 caractères)</p>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4 animate-in zoom-in duration-500">
              <Check className="w-8 h-8 text-emerald-600" strokeWidth={3} />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Mot de passe modifié</h3>
            <p className="text-sm text-slate-500 mt-1">Utilisez le nouveau lors de votre prochaine connexion</p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Mot de passe actuel</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoFocus
                  className="w-full px-4 py-3 pr-11 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Nouveau mot de passe</label>
              <input
                type={show ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Confirmer</label>
              <input
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { reset(); onClose(); }} className="px-5 py-2.5 rounded-full text-slate-600 hover:bg-slate-100 font-medium">Annuler</button>
              <button
                type="submit"
                disabled={loading || !current || !next || !confirm}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 disabled:cursor-not-allowed text-white font-semibold rounded-full"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Modification...</> : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
